import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useTriageBatch,
  useCheckTriageItem,
  useExportTriageItem,
  useUpdateTriageItem,
} from '../hooks/useTriage';
import { StatusBadge } from '../components/StatusBadge';
import type { TriageItem } from '../types';

const QUEUE = 'missing-ship-memo';

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function normalizedStatus(value?: string): string {
  return (value || '').toLowerCase().replace(/\s+/g, '');
}

function isPrepareShipment(value?: string): boolean {
  return normalizedStatus(value) === 'prepareshipment';
}

function statusPill(value?: string, emptyLabel = '—') {
  if (!value) {
    return <span className="text-gray-400">{emptyLabel}</span>;
  }

  const normalized = normalizedStatus(value);
  const className =
    normalized === 'rejected'
      ? 'bg-green-100 text-green-800'
      : normalized === 'prepareshipment'
        ? 'bg-amber-100 text-amber-800'
        : normalized === 'processing' || normalized === 'received' || normalized === 'staged'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-gray-100 text-gray-700';

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {value}
    </span>
  );
}

function ShipMemoRow({ date, item }: { date: string; item: TriageItem }) {
  const check = useCheckTriageItem(QUEUE, date);
  const exportMemo = useExportTriageItem(QUEUE, date);
  const update = useUpdateTriageItem(QUEUE, date);

  const [notes, setNotes] = useState(item.notes ?? '');
  useEffect(() => {
    setNotes(item.notes ?? '');
  }, [item.notes]);
  const notesChanged = notes !== (item.notes ?? '');

  const busy = check.isPending || exportMemo.isPending || update.isPending;
  const resolved = item.status === 'resolved';
  const canExport =
    !resolved &&
    isPrepareShipment(item.omsStatus) &&
    item.shipMemoExportStatus !== 'submitted' &&
    item.shipMemoExportStatus !== 'verified';
  const actionError =
    exportMemo.data?.error ||
    (exportMemo.error instanceof Error ? exportMemo.error.message : undefined) ||
    (check.error instanceof Error ? check.error.message : undefined) ||
    (item.shipMemoExportStatus === 'error' ? item.shipMemoExportError : undefined);
  const shopifyOrderId = item.shopifyOrderId || item.omsSalesOrderId;
  const shopifyOrderLabel = item.shopifyOrderName || shopifyOrderId;

  return (
    <tr className={resolved ? 'bg-green-50/40' : ''}>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
        {item.itemId}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {formatDateTime(item.sourceData.created_at)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <StatusBadge status={item.status === 'resolved' ? 'resolved' : 'pending'} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {item.reason || '—'}
        {resolved && item.resolvedBy && (
          <span className="block text-xs text-gray-400">
            by {item.resolvedBy} {item.resolvedAt ? `- ${formatDateTime(item.resolvedAt)}` : ''}
          </span>
        )}
        {actionError && (
          <span className="mt-1 block max-w-md rounded border border-red-200 bg-red-50 px-2 py-1 font-mono text-xs text-red-700 whitespace-pre-wrap">
            {actionError}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        {shopifyOrderId ? (
          <a
            href={`https://admin.shopify.com/store/skimsbody/orders/${shopifyOrderId}`}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            {shopifyOrderLabel}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        <div className="space-y-1">
          {statusPill(item.omsStatus)}
          {item.omsLastCheckedAt && (
            <div className="text-xs text-gray-400">{formatDateTime(item.omsLastCheckedAt)}</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        <div className="space-y-1">
          {statusPill(item.airbossStatus, 'Missing')}
          {item.shipMemoExportStatus && (
            <div
              className={
                item.shipMemoExportStatus === 'verified'
                  ? 'text-xs font-medium text-green-700'
                  : item.shipMemoExportStatus === 'submitted'
                    ? 'text-xs font-medium text-blue-700'
                    : 'text-xs font-medium text-red-700'
              }
            >
              Export {item.shipMemoExportStatus}
            </div>
          )}
          {item.shipMemoExportedAt && (
            <div className="text-xs text-gray-400">{formatDateTime(item.shipMemoExportedAt)}</div>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes..."
            className="w-44 rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-blue-400 focus:outline-none"
          />
          {notesChanged && (
            <button
              onClick={() => update.mutate({ itemId: item.itemId, status: item.status, notes })}
              disabled={busy}
              className="px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              Save
            </button>
          )}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {!resolved && (
            <button
              onClick={() => check.mutate(item.itemId)}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
            >
              <svg
                className={`w-3.5 h-3.5 ${check.isPending ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {check.isPending ? 'Checking...' : 'Check Status'}
            </button>
          )}
          {canExport && (
            <button
              onClick={() => {
                if (window.confirm('Export this PrepareShipment ship memo to AirBoss?')) {
                  exportMemo.mutate(item.itemId);
                }
              }}
              disabled={busy}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md disabled:opacity-50 ${
                item.shipMemoExportStatus === 'error'
                  ? 'bg-red-50 text-red-700 hover:bg-red-100'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {exportMemo.isPending
                ? 'Exporting...'
                : item.shipMemoExportStatus === 'error'
                  ? 'Retry Export'
                  : 'Export'}
            </button>
          )}
          <button
            onClick={() =>
              update.mutate({
                itemId: item.itemId,
                status: resolved ? 'pending' : 'resolved',
                ...(notesChanged ? { notes } : {}),
              })
            }
            disabled={busy}
            className={`px-2.5 py-1 text-xs font-medium rounded-md disabled:opacity-50 ${
              resolved
                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            {resolved ? 'Reopen' : 'Resolve'}
          </button>
        </div>
      </td>
    </tr>
  );
}

export function ShipMemoDetail() {
  const { date } = useParams<{ date: string }>();
  const { data, isLoading, error } = useTriageBatch(QUEUE, date || '');

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          Error loading batch: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Link to="/ship-memos" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
          Back to Missing Ship Memos
        </Link>
      </div>
    );
  }

  const formattedDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const progressPercent =
    data?.batch && data.batch.totalItems > 0
      ? Math.round((data.batch.resolvedItems / data.batch.totalItems) * 100)
      : 0;

  // Pending first, then by id for a stable order
  const items = [...(data?.items ?? [])].sort((a, b) =>
    a.status === b.status ? a.itemId.localeCompare(b.itemId) : a.status === 'pending' ? -1 : 1
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/ship-memos" className="text-gray-400 hover:text-gray-600 transition-colors">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{formattedDate}</h1>
          {data?.batch && (
            <p className="text-sm text-gray-500">
              Imported {formatDateTime(data.batch.importedAt)}
            </p>
          )}
        </div>
        {data?.batch && <StatusBadge status={data.batch.status} />}
      </div>

      {/* Progress */}
      {data?.batch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">
              {data.batch.resolvedItems} / {data.batch.totalItems} memos resolved
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                data.batch.status === 'complete'
                  ? 'bg-green-500'
                  : data.batch.status === 'partial'
                    ? 'bg-blue-500'
                    : 'bg-yellow-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500 text-right">{progressPercent}% complete</p>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No ship memos in this batch.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'Ship Memo ID',
                  'Created (CHQ)',
                  'Status',
                  'Reason',
                  'Shopify Order',
                  'OMS',
                  'AirBoss',
                  'Notes',
                  'Actions',
                ].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <ShipMemoRow key={item.itemId} date={date!} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
